// WshcChecklistSection — WSHC-template (landscape) rendering of report §5: Main Checklist,
// Audit Highlights and the Interview Checklist grid. PRINT-ONLY: used by /report-print/:token?part=wshc.
// Auditor data comes from the frozen snapshot; static template text (questions, DR/IP/PI, guidance,
// evidence notes) comes from rmChecklistWshc (V3.3). Colours/fills mirror the official Excel exactly:
//   GREEN header/section = accent6 tint .6 (#b8dcab) · GREY auditor-input cells = #d9d9d9 ·
//   interview labels = #f2f2f2 · interviewee pairs = accent1/2/3/4 tint .8 · AH interview ref = #ecd5e9.
import React from 'react'
import { WSHC_MAIN, WSHC_AH, WSHC_INTERVIEW_QUESTIONS, WSHC_REVISION } from '../lib/rmChecklistWshc'

const bd = '1px solid #444'
const GREEN = '#b8dcab'   // header + section rows
const GREY = '#d9d9d9'    // auditor input columns: Results / Annex / Remarks
const LABEL = '#f2f2f2'   // interview left-labels + Reference/Question headers
const AH_REF = '#ecd5e9'  // Audit-Highlight interview reference cells (mauve/pink)
const PAIR_COLORS = ['#d0dfe6', '#fbe3d6', '#d1e1d3', '#cfecf7']  // interviewee 1..4
// All text BLACK (a global `th{color}` rule would otherwise tint headers teal).
const th = { border: bd, padding: '3px 4px', background: GREEN, color: '#000', fontWeight: 700, textAlign: 'center', verticalAlign: 'middle', fontSize: 9.5, boxSizing: 'border-box' }
const td = { border: bd, padding: '3px 4px', color: '#000', verticalAlign: 'top', fontSize: 9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', boxSizing: 'border-box' }
const secRow = { border: bd, padding: '3px 4px', background: GREEN, color: '#000', fontWeight: 700, fontSize: 9.5, boxSizing: 'border-box' }
const nowrap = { whiteSpace: 'nowrap' }                                   // narrow cols never split ("Yes"/"S/No.")
const inputTd = { ...td, background: GREY, textAlign: 'center', fontWeight: 700 }
const noBreak = { pageBreakInside: 'avoid', breakInside: 'avoid' }        // a row never cuts mid-content
const legendTh = { ...th, fontSize: 7.5, whiteSpace: 'pre-line' }
const DR_LEGEND = 'DR = Document Review\nIP = Interview Personnel\nPI = Physical Inspection'
const has = v => v != null && String(v).trim() !== ''
const fmtD = v => { try { return v ? new Date(v).toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '' } catch { return v || '' } }

export default function WshcChecklistSection({ doc }) {
  const { job = {}, snapshot = {} } = doc || {}
  const s = snapshot?.snapshot || snapshot || {}
  const checklist = (s.checklist || []).filter(Boolean)
  const byClause = {}
  checklist.forEach(it => { if (it.clause_ref) byClause[String(it.clause_ref).trim()] = it })
  const interviews = (s.interviews || s.interview_rows || []).filter(Boolean)
  const auditors = [job.auditor_name, job.auditor2_name].filter(Boolean).join(' / ')

  // Snapshot clauses not covered by the static template (safety net: nothing recorded is ever dropped)
  const knownMain = new Set(); WSHC_MAIN.forEach(g => g.items.forEach(it => knownMain.add(it.clause)))
  const extras = checklist.filter(it => {
    const c = String(it.clause_ref || '').trim()
    return c && !knownMain.has(c) && !/^AH\s*\d+$/i.test(c)
  })

  const res = it => (it && it.result) || ''
  // "1" marks the result column (WSHC Excel convention) — plain text, renders in any font.
  const tick = (it, want) => res(it) === want ? '1' : ''

  // ---- interview pivot: interviewees across columns (colour cycle), template questions down rows ----
  const keyOf = iv => `${String(iv.interviewee_name || '').trim().toLowerCase()}|${String(iv.designation || '').trim().toLowerCase()}`
  const allPeople = []
  interviews.forEach(iv => {
    const k = keyOf(iv)
    const existing = allPeople.find(p => p.k === k)
    if (!existing) allPeople.push({ k, name: String(iv.interviewee_name || '').trim(), desig: String(iv.designation || '').trim(), jobdesc: String(iv.job_description || '').trim() })
    else { // richer values win (old rows sometimes carry the name on some rows only)
      if (!existing.name && iv.interviewee_name) existing.name = String(iv.interviewee_name).trim()
      if (!existing.jobdesc && iv.job_description) existing.jobdesc = String(iv.job_description).trim()
    }
  })
  // A person column must be a REAL interviewee: named, or with at least one recorded response.
  const people = allPeople.filter(p => has(p.name) || interviews.some(iv => keyOf(iv) === p.k && has(iv.response)))
  const normRef = v => String(v || '').replace(/\s+/g, '').toUpperCase()
  const rowFor = (ref, k) => {
    const rows = interviews.filter(iv => normRef(iv.clause_ref) === normRef(ref) && keyOf(iv) === k)
    return rows.find(r => has(r.response)) || rows[0] || null
  }
  const stripInstr = q => String(q || '').replace(/^\s*Interview[^:]*:\s*/i, '').trim()
  const extraIvRefs = [...new Set(interviews.map(iv => String(iv.clause_ref || '').trim()).filter(r => r && !WSHC_INTERVIEW_QUESTIONS.find(q => normRef(q.ref) === normRef(r))))]
  const ivRows = [...WSHC_INTERVIEW_QUESTIONS, ...extraIvRefs.map(r => ({ ref: r, question: (interviews.find(iv => String(iv.clause_ref || '').trim() === r) || {}).question || '' }))]
  const isAhRef = ref => /^AH/i.test(String(ref || '').trim())
  const blocks = []
  for (let i = 0; i < people.length; i += 4) blocks.push(people.slice(i, i + 4))
  if (blocks.length === 0) blocks.push([])

  // MANUAL / Excel fail-safe audit: the auditor completed the official checklist + interview sheet on the
  // manual forms, which are APPENDED to the package. Rendering the empty system templates here would put
  // blank checklist pages in front of the real completed ones — print the §5 note instead (mirrors
  // ReportDocument's own isManual handling). Snapshot-driven, so stored packages are unaffected.
  if ((s.execution_mode || 'system') === 'manual') {
    return (
      <div style={{ fontFamily: 'Arial', color: '#000' }}>
        <h2 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', padding: '6px 10px', background: '#2b2b2b', color: '#fff' }}>
          5. Audit Checklist &amp; Other Considerations, Interview Personnel Sheet &amp; Audit Evidences
        </h2>
        <p style={{ fontSize: 12, margin: '10px 0 0', lineHeight: 1.6 }}>
          This audit was conducted using the manual / Excel fail-safe method. Reason: {s.manual_failsafe_reason || '—'}.
          The completed RM Audit Checklist, Audit Highlights and Interview Personnel Sheet are appended after this report,
          together with the implementation evidence and supporting documents.
        </p>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: 'Arial', color: '#000' }}>
      <h2 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 10px', padding: '6px 10px', background: '#2b2b2b', color: '#fff' }}>
        5. Audit Checklist &amp; Other Considerations, Interview Personnel Sheet &amp; Audit Evidences
      </h2>

      {/* ── MAIN CHECKLIST ── */}
      <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 6px' }}>RM Audit Checklist (Revision {s.checklist_revision || WSHC_REVISION})</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 34 }} /><col style={{ width: '19%' }} /><col style={{ width: 52 }} />
          <col style={{ width: 26 }} /><col style={{ width: 26 }} />
          <col style={{ width: '25%' }} /><col style={{ width: '19%' }} /><col style={{ width: 44 }} /><col />
        </colgroup>
        <thead style={{ display: 'table-header-group' }}>
          <tr>
            <th style={{ ...th, ...nowrap }}>S/No.</th><th style={th}>Audit Question</th>
            <th style={legendTh}>{DR_LEGEND}</th>
            <th style={th} colSpan={2}>Results</th>
            <th style={th}>Guidance for RM Auditor</th>
            <th style={th} colSpan={2}>Supporting Evidence</th>
            <th style={th}>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {WSHC_MAIN.map((g, gi) => (
            <React.Fragment key={gi}>
              <tr style={noBreak}>
                <td style={secRow}>{g.no}</td>
                <td style={secRow} colSpan={2}>{g.title}</td>
                <td style={{ ...secRow, ...nowrap, textAlign: 'center' }}>Yes</td>
                <td style={{ ...secRow, ...nowrap, textAlign: 'center' }}>No</td>
                <td style={secRow}></td>
                <td style={{ ...secRow, ...nowrap, textAlign: 'center' }}>Notes</td>
                <td style={{ ...secRow, ...nowrap, textAlign: 'center' }}>Annex</td>
                <td style={secRow}></td>
              </tr>
              {g.items.map((it, i) => {
                const r = byClause[it.clause]
                return (
                  <React.Fragment key={i}>
                  {it.subheader && (
                    <tr style={noBreak}><td style={{ ...secRow, background: '#e6efdb', fontStyle: 'italic' }} colSpan={9}>{it.subheader}</td></tr>
                  )}
                  <tr style={noBreak}>
                    <td style={td}>{it.clause}{it.tag ? <div style={{ fontSize: 7.5, fontWeight: 700 }}>{it.tag}</div> : null}</td>
                    <td style={td}>{it.question}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{it.dr || ''}</td>
                    <td style={inputTd}>{tick(r, 'Yes')}{res(r) === 'N/A' ? 'NA' : ''}</td>
                    <td style={inputTd}>{tick(r, 'No')}</td>
                    <td style={td}>{it.guidance || ''}</td>
                    <td style={td}>{it.notes || ''}</td>
                    <td style={inputTd}>{r && res(r) === 'Yes' && has(r.annex_ref) ? r.annex_ref : ''}</td>
                    <td style={{ ...td, background: GREY }}>{(r && r.remarks) || ''}</td>
                  </tr>
                  </React.Fragment>
                )
              })}
            </React.Fragment>
          ))}
          {extras.length > 0 && (
            <React.Fragment>
              <tr style={noBreak}><td style={secRow} colSpan={9}>Additional items recorded in this audit</td></tr>
              {extras.map((r, i) => (
                <tr key={'x' + i} style={noBreak}>
                  <td style={td}>{r.clause_ref}</td>
                  <td style={td} colSpan={2}>{r.item_text || ''}</td>
                  <td style={inputTd}>{tick(r, 'Yes')}</td>
                  <td style={inputTd}>{tick(r, 'No')}</td>
                  <td style={td} colSpan={2}></td>
                  <td style={inputTd}>{has(r.annex_ref) ? r.annex_ref : ''}</td>
                  <td style={{ ...td, background: GREY }}>{r.remarks || ''}</td>
                </tr>
              ))}
            </React.Fragment>
          )}
        </tbody>
      </table>

      {/* ── AUDIT HIGHLIGHTS ── */}
      <div style={{ breakBefore: 'page', pageBreakBefore: 'always' }}>
        <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 6px' }}>Audit Highlights</div>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 34 }} /><col style={{ width: 70 }} /><col style={{ width: '16%' }} /><col style={{ width: 52 }} />
            <col style={{ width: 26 }} /><col style={{ width: 26 }} /><col style={{ width: 28 }} />
            <col style={{ width: '25%' }} /><col style={{ width: '17%' }} /><col style={{ width: 44 }} /><col />
          </colgroup>
          <thead style={{ display: 'table-header-group' }}>
            <tr>
              <th style={{ ...th, ...nowrap }}>S/No.</th><th style={th}>Audit Highlight</th><th style={th}>Audit Question</th>
              <th style={legendTh}>{DR_LEGEND}</th>
              <th style={{ ...th, ...nowrap }}>Yes</th><th style={{ ...th, ...nowrap }}>No</th><th style={{ ...th, ...nowrap }}>NA</th>
              <th style={th}>Guidance for RM Auditor</th>
              <th style={th}>Supporting Evidence — Notes</th><th style={th}>Annex</th><th style={th}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {WSHC_AH.map((a, i) => {
              const r = byClause['AH' + a.no]
              return (
                <tr key={i} style={noBreak}>
                  <td style={td}>{a.no}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{a.name}</td>
                  <td style={td}>{a.question}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{a.dr || ''}</td>
                  <td style={inputTd}>{tick(r, 'Yes')}</td>
                  <td style={inputTd}>{tick(r, 'No')}</td>
                  <td style={inputTd}>{res(r) === 'N/A' ? '1' : ''}</td>
                  <td style={td}>{a.guidance || ''}</td>
                  <td style={td}>{a.notes || ''}</td>
                  <td style={inputTd}>{r && res(r) === 'Yes' && has(r.annex_ref) ? r.annex_ref : ''}</td>
                  <td style={{ ...td, background: GREY }}>{(r && r.remarks) || ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── INTERVIEW CHECKLIST GRID ── */}
      <div style={{ breakBefore: 'page', pageBreakBefore: 'always' }}>
        <div style={{ fontSize: 12, fontWeight: 700, margin: '8px 0 6px' }}>Interview Checklist</div>
        {blocks.map((blk, bi) => (
          <table key={bi} style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', marginBottom: 14, pageBreakBefore: bi === 0 ? 'auto' : 'always', breakBefore: bi === 0 ? 'auto' : 'page' }}>
            <colgroup>
              <col style={{ width: 56 }} /><col style={{ width: '22%' }} />
              {blk.map((_, i) => <React.Fragment key={i}><col /><col /></React.Fragment>)}
            </colgroup>
            {/* Identity + column headers repeat on every page this block spans (table-header-group). */}
            <thead style={{ display: 'table-header-group' }}>
              {[['Date of Interview', () => fmtD(job.audit_date)],
                ['Name of Interviewer', () => auditors],
                ['Name of Interviewee', p => p.name],
                ['Position/ Designation of Interviewee', p => p.desig],
                ['Job description of Interviewee', p => p.jobdesc]].map(([label, fn], ri) => (
                <tr key={ri} style={noBreak}>
                  <td style={{ ...td, background: LABEL, fontWeight: 700 }} colSpan={2}>{label}</td>
                  {blk.map((p, i) => <td key={i} style={td} colSpan={2}>{typeof fn === 'function' ? (fn.length ? fn(p) : fn()) : ''}</td>)}
                </tr>
              ))}
              <tr style={noBreak}>
                <td style={{ ...th, background: LABEL, whiteSpace: 'normal', wordBreak: 'break-word' }}>Reference</td>
                <td style={{ ...th, background: LABEL, whiteSpace: 'normal', wordBreak: 'break-word' }}>Question</td>
                {blk.map((p, i) => (
                  <React.Fragment key={i}>
                    <td style={{ ...th, background: PAIR_COLORS[i % PAIR_COLORS.length] }}>Question</td>
                    <td style={{ ...th, background: PAIR_COLORS[i % PAIR_COLORS.length] }}>Response</td>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {ivRows.map((q, qi) => (
                <tr key={qi} style={noBreak}>
                  <td style={{ ...td, background: isAhRef(q.ref) ? AH_REF : GREEN, fontWeight: 700, textAlign: 'center' }}>{q.ref}</td>
                  <td style={td}>{q.question}</td>
                  {blk.map((p, i) => {
                    const r = rowFor(q.ref, p.k)
                    return (
                      <React.Fragment key={i}>
                        <td style={td}>{r ? (stripInstr(r.question) || stripInstr(q.question)) : ''}</td>
                        <td style={td}>{(r && r.response) || ''}</td>
                      </React.Fragment>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
        <div style={{ fontSize: 9.5, color: '#000' }}>Interviews conducted on {fmtD(job.audit_date)} by {auditors || '—'}.</div>
      </div>
    </div>
  )
}
